/**
 * Pure date/age helpers for the Andys landing (#1240). No React/CSS imports, so
 * they're trivially unit-testable (see andysDates.test.js). Shared by the custom
 * DatePicker (ISO parse/format) and the rent prefill bridge (mapAge).
 */

/** Parse 'YYYY-MM-DD' → { y, mo (0-based), d } or null for empty/invalid. */
export const parseISO = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v || '');
  return m ? { y: +m[1], mo: +m[2] - 1, d: +m[3] } : null;
};

/** Build 'YYYY-MM-DD' from year, 0-based month, day. */
export const toISO = (y, mo, d) =>
  `${y}-${String(mo + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

/** Today as 'YYYY-MM-DD' in the viewer's local time. */
export const todayISO = () => {
  const n = new Date();
  return toISO(n.getFullYear(), n.getMonth(), n.getDate());
};

/** 'YYYY-MM-DD' `n` days after `v` (n may be negative); '' for empty/invalid. */
export const addDaysISO = (v, n) => {
  const p = parseISO(v);
  if (!p) return '';
  const d = new Date(p.y, p.mo, p.d + n);
  return toISO(d.getFullYear(), d.getMonth(), d.getDate());
};

/**
 * Lead time of Andys' Wheelsystems engine in days (its `leadDays`, 2 on
 * andys-motors.com/car-rental): its calendar starts the day after tomorrow, and
 * the rent prefill bridge skips the auto-search for an earlier pick-up (#1469).
 */
export const RENT_LEAD_DAYS = 2;

/**
 * The first pick-up day in the viewer's local time — the `min` of the landing's
 * rent date pickers. Past days give no cars (#1255); earlier days than the
 * engine's lead time only fill its form without a search (#1469).
 */
export const firstPickupISO = () => addDaysISO(todayISO(), RENT_LEAD_DAYS);

/** Sunday? For a 'YYYY-MM-DD'; false for empty/invalid. */
export const isSundayISO = (v) => {
  const p = parseISO(v);
  return !!p && new Date(p.y, p.mo, p.d).getDay() === 0;
};

/**
 * Whole days from one 'YYYY-MM-DD' to another (negative when `to` is earlier);
 * null if either is empty/invalid. Counted on UTC dates, so a DST change in
 * between doesn't shave off an hour and round the day away. The landing's rent
 * search event reports the rental length and how far ahead the pick-up is with
 * it (#1482).
 */
export const daysBetween = (from, to) => {
  const a = parseISO(from);
  const b = parseISO(to);
  if (!a || !b) return null;
  return Math.round((Date.UTC(b.y, b.mo, b.d) - Date.UTC(a.y, a.mo, a.d)) / 86_400_000);
};

/** 'YYYY-MM-DD' → 'DD.MM.YYYY' for display; '' for empty/invalid. */
export const fmtDisplay = (v) => {
  const p = parseISO(v);
  return p
    ? `${String(p.d).padStart(2, '0')}.${String(p.mo + 1).padStart(2, '0')}.${p.y}`
    : '';
};

/**
 * 'YYYY-MM-DD' → 'DD/MM/YYYY' — the Wheelsystems engine's `dateFormat`. The
 * rent prefill bridge writes this into the engine's date-picker buttons, whose
 * `.value` `SearchCars()` reads verbatim (#1252). '' for empty/invalid.
 */
export const toEngineDate = (v) => {
  const p = parseISO(v);
  return p
    ? `${String(p.d).padStart(2, '0')}/${String(p.mo + 1).padStart(2, '0')}/${p.y}`
    : '';
};

/**
 * Every half hour, 00:00–23:30 — the Wheelsystems engine's own time options
 * (`#time1` / `#time2`, `wheelsTimeStep` 30). The rent date pickers offer the
 * same, so any choice is one the engine has (#1469).
 */
export const HALF_HOUR_TIMES = Array.from({ length: 48 }, (_, i) => {
  const h = String(Math.floor(i / 2)).padStart(2, '0');
  return `${h}:${i % 2 ? '30' : '00'}`;
});

/**
 * Rent pick-up and return times on the landing: Andys' delivery schedule,
 * 09:30–23:00 (their car-rental terms; bookings outside it may be moved). A
 * subset of the engine's grid, so the handoff still finds every time (#1469).
 */
export const RENT_TIMES = HALF_HOUR_TIMES.filter((t) => t >= '09:30' && t <= '23:00');

/** The engine's default pick-up and return time (`defHour`). */
export const DEFAULT_RENT_TIME = '12:00';

/** 'HH:MM' on the engine's half-hour grid, or null. */
export const parseRentTime = (v) =>
  typeof v === 'string' && HALF_HOUR_TIMES.includes(v) ? v : null;

/**
 * Landing age RANGE → the Wheelsystems engine's single-year "Driver's age"
 * select: its low bound ('23'→'23', '24-69'→'24', '70+'→'70', #1469).
 * Returns null for empty/invalid.
 */
export const mapAge = (range) => {
  const m = range && String(range).match(/\d+/);
  return m ? m[0] : null;
};
